import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '../lib/utils';
import { ReleaseBodyPointerEventsGuard } from './useReleaseBodyPointerEvents';
import { ModalPresenceGuard } from 'store/modal-presence';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-[3400] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /**
   * Extra classes for the scrim behind the panel. The overlay is rendered by
   * this component, so without this a consumer has no way to reach it short of
   * hand-rolling its own portal (see `CalculationBuilder/DrawerModal.tsx`).
   *
   * Deliberately additive: the default `bg-black/80` is unchanged, because the
   * ~10 `Dialog` and ~50 `AlertDialog` consumers were never designed against
   * anything else. Content-label dialogs pass the Figma scrim through here.
   */
  overlayClassName?: string;
  /**
   * Extra classes for the close X. Additive in exactly the same way as
   * `overlayClassName`: omitted, the button keeps the `right-4 top-4` inset
   * every other consumer is laid out against.
   *
   * It exists because the button is rendered by this component, so a design
   * that insets it differently (Figma puts the content-label dialogs' X at
   * 24/20 in a 28px box) cannot otherwise reach it. `cn()` merges, so a
   * caller's `right-*`/`top-*` REPLACES the default rather than racing it in
   * the stylesheet.
   */
  closeClassName?: string;
  /** Leaves the close X out, for a caller that renders its own (e.g. in its header, first in the Tab order). */
  hideCloseButton?: boolean;
};

const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  ({ className, children, overlayClassName, closeClassName, hideCloseButton = false, ...props }, ref) => {
    return (
      <DialogPortal>
        <DialogOverlay className={overlayClassName} />
        {/* Centred by auto margins, never a `-50%` transform (fractional matrix -> no pixel snapping)
            and never a wrapper (`DialogPortal` gives each child its own animation-gated `Presence`). */}
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'fixed bottom-0 left-0 right-[var(--app-assistant-inset,0px)] top-0 z-[3400] m-auto grid h-fit w-full max-w-lg gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 sm:rounded-lg',
            className,
          )}
          {...props}
        >
          {/* Mounts/unmounts with the Content's presence -> releases a stuck Radix body pointer-events
              lock when the dialog closes (the always-mounted wrapper never unmounts on close). */}
          <ReleaseBodyPointerEventsGuard />
          <ModalPresenceGuard />
          {children}
          {hideCloseButton ? null : (
            <DialogPrimitive.Close
              className={cn(
                'absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground',
                closeClassName,
              )}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-medium leading-4 tracking-normal', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
